import { useState } from "react";
import type { AnalyticsRangePreset } from "@platform/shared";

export interface RangeSelection {
  range: AnalyticsRangePreset;
  from?: string;
  to?: string;
}

const PRESETS: { value: Exclude<AnalyticsRangePreset, "custom">; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
  { value: "90d", label: "90 days" },
  { value: "this_month", label: "This month" },
  { value: "last_month", label: "Last month" },
];

/** Query string for the analytics endpoints. */
export function rangeQuery(sel: RangeSelection): string {
  if (sel.range === "custom" && sel.from && sel.to) {
    return `from=${encodeURIComponent(sel.from)}&to=${encodeURIComponent(sel.to)}`;
  }
  return `range=${sel.range === "custom" ? "30d" : sel.range}`;
}

const buttonClass = (active: boolean) =>
  `rounded px-3 py-1.5 text-xs font-medium transition-colors ${
    active ? "bg-brand-700 text-white" : "text-ink-soft hover:text-ink"
  }`;

/** Preset buttons plus a custom from/to range (UTC calendar days). */
export function RangePicker({ value, onChange }: { value: RangeSelection; onChange: (v: RangeSelection) => void }) {
  const [from, setFrom] = useState(value.from ?? "");
  const [to, setTo] = useState(value.to ?? "");
  const [showCustom, setShowCustom] = useState(value.range === "custom");
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="flex flex-col items-end gap-2">
      <div className="flex flex-wrap gap-1 rounded-md border border-line bg-paper-raised p-1">
        {PRESETS.map((p) => (
          <button
            key={p.value}
            type="button"
            onClick={() => {
              setShowCustom(false);
              onChange({ range: p.value });
            }}
            className={buttonClass(value.range === p.value)}
          >
            {p.label}
          </button>
        ))}
        <button type="button" onClick={() => setShowCustom(true)} className={buttonClass(value.range === "custom" || showCustom)}>
          Custom
        </button>
      </div>
      {showCustom && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-ink-soft">
          <input
            type="date"
            value={from}
            max={to || today}
            onChange={(e) => setFrom(e.target.value)}
            className="rounded-md border border-line bg-paper-raised px-2 py-1 text-ink"
            aria-label="From date"
          />
          <span>to</span>
          <input
            type="date"
            value={to}
            min={from || undefined}
            max={today}
            onChange={(e) => setTo(e.target.value)}
            className="rounded-md border border-line bg-paper-raised px-2 py-1 text-ink"
            aria-label="To date"
          />
          <button
            type="button"
            disabled={!from || !to || from > to}
            onClick={() => onChange({ range: "custom", from, to })}
            className="rounded bg-brand-700 px-3 py-1.5 font-medium text-white disabled:opacity-40"
          >
            Apply
          </button>
        </div>
      )}
    </div>
  );
}
